import styled from "@emotion/styled";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Expand_DashSearchRightPanel } from "@mirage/analytics/events/types/expand_dash_search_right_panel";
import { LoadingResultList } from "@mirage/search/SearchResults/LoadingResult";
import { getVerification } from "@mirage/shared/curations";
import { SearchTimeoutError } from "@mirage/shared/errors/classes/search";
import i18n from "@mirage/translations";
import { Error } from "./Error";
import { useInjectedResultRows } from "./hooks/useInjectedResultRows";
import { NoResults } from "./NoResults";
import ResultList from "./ResultList";
import { ResultRow } from "./ResultRow/ResultRow";

import type { dash } from "@dropbox/api-v2-client";
import type { ContentIconProps } from "@dropbox/dash-component-library";
import type { LaunchMethod } from "@mirage/analytics/events/enums/launch_method";
import { PAP_Click_DashMultimedia } from "@mirage/analytics/events/types/click_dash_multimedia";
import type { SearchResult } from "@mirage/service-dbx-api";
import type { PersonObject } from "@mirage/service-people/types";
import {
  concatActiveFilterBindings,
  type SearchFilter,
} from "@mirage/shared/search/search-filters";
import { memo } from "react";
import type { ResultActionConfig } from "./ResultRow";

import styles from "./SearchResults.module.css";

const STAGGER_LOADING_RESULTS_MS = 83;
const RESULT_ANIMATION_LIMIT = 10;

type SearchResultsProps = {
  query: string;
  results: SearchResult[];
  loading: boolean;
  error: Error | null;
  getActionConfig: (
    result: SearchResult,
    results: SearchResult[],
  ) => ResultActionConfig;
  onListItemView?: (item: SearchResult) => void;
  onCopyResult: (result: SearchResult) => void;
  activeFilters?: SearchFilter[];
  annotationsEnabled?: boolean;
  iconSize?: ContentIconProps["size"];
  peekResultActionsEnabled?: boolean;
  onClickPerson?: (person: PersonObject) => void;
  resetAllFilters: () => void;
  onResubmitSearch: () => void;
  onAddToStack: (result: SearchResult) => void;
  onSummarize: (result: SearchResult) => void;
  cachedVerifications?: { [resultId: string]: dash.Curation | null };
};

export const SearchResults = memo(
  ({
    query,
    results,
    loading,
    error,
    getActionConfig,
    onListItemView,
    onCopyResult,
    activeFilters = [],
    annotationsEnabled,
    iconSize,
    peekResultActionsEnabled = false,
    onClickPerson,
    resetAllFilters,
    onResubmitSearch,
    onAddToStack,
    onSummarize,
    cachedVerifications = {},
  }: SearchResultsProps) => {
    const { reportPapEvent, searchSessionManager } =
      useMirageAnalyticsContext();
    const injectedResult = useInjectedResultRows();

    const isChatSearch = activeFilters.some((filter) => {
      return filter.id === "slack" || filter.id === "chat";
    });

    const handleGetActionConfig = (result: SearchResult) => {
      return getActionConfig(result, results);
    };

    const logOpenResultDropdown = (isOpen: boolean) => {
      if (!isOpen) return;
      reportPapEvent(
        PAP_Expand_DashSearchRightPanel({
          featureLine: "search",
          actionSurface: "serp",
        }),
      );
    };

    const renderResultRow = (
      result: SearchResult,
      index: number,
      delayMs: number = 0,
    ) => {
      const resultActionConfig = handleGetActionConfig(result);

      const onLaunch = (launchMethod: LaunchMethod) => {
        if (result.previewUrl) {
          reportPapEvent(
            PAP_Click_DashMultimedia({
              searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
              fileTypeCategory: result.fileTypeInfo?.id,
              activeFilters: concatActiveFilterBindings(activeFilters),
            }),
          );
        }
        resultActionConfig.launchAction?.onClick?.(launchMethod);
      };
      return (
        <ResultRow
          key={result.uuid || Math.random().toString()}
          query={query}
          result={result}
          verification={
            result.uuid in cachedVerifications
              ? // If the result is cached (user just previously updated it), use the cached value
                (cachedVerifications[result.uuid] ?? undefined)
              : getVerification(result)
          }
          onLaunch={onLaunch}
          resultActionConfig={resultActionConfig}
          onOpenDropdown={logOpenResultDropdown}
          onShownResult={onListItemView}
          annotationsEnabled={annotationsEnabled}
          resultPosition={index}
          peekResultActionsEnabled={peekResultActionsEnabled && index == 0}
          iconSize={iconSize}
          activeFilters={activeFilters}
          onClickPerson={onClickPerson}
          onCopyResult={onCopyResult}
          delayMs={delayMs}
          animate={true}
          onAddToStack={onAddToStack}
          onSummarize={onSummarize}
        />
      );
    };

    const renderResults = () => {
      const resultList = results.map((result, i) =>
        renderResultRow(
          result,
          i,
          i < RESULT_ANIMATION_LIMIT ? i * STAGGER_LOADING_RESULTS_MS : 0,
        ),
      );

      if (injectedResult) {
        const { component: Component, position, props = {} } = injectedResult;
        // Calculate the animation delay for the tip based on its position
        const injectedDelay =
          position < RESULT_ANIMATION_LIMIT
            ? position * STAGGER_LOADING_RESULTS_MS
            : 0;

        resultList.splice(
          position,
          0,
          <Component
            key="injected-result"
            {...props}
            animate={true}
            animateDelayMs={injectedDelay}
          />,
        );
      }

      if (isChatSearch) {
        return (
          <div
            className={
              loading
                ? styles.searchResultsList
                : styles.searchResultsListFadeIn
            }
          >
            {resultList}
          </div>
        );
      } else {
        return resultList;
      }
    };

    if (loading && (!results.length || isChatSearch)) {
      return (
        <CenteredContainer>
          <LoadingResultList count={8} staggerMs={STAGGER_LOADING_RESULTS_MS} />
        </CenteredContainer>
      );
    }

    if (query == "" && !activeFilters.length && !loading) {
      return <div></div>;
    }

    if (!error && !results.length && !loading) {
      return (
        <NoResults
          activeFilters={activeFilters}
          resetAllFilters={resetAllFilters}
        />
      );
    }

    return (
      <>
        {error?.name === SearchTimeoutError.name ? (
          <Error
            title={i18n.t("error")}
            subtitle={i18n.t("search_timed_out")}
            actionText={i18n.t("retry_search")}
            onActionClick={onResubmitSearch}
          />
        ) : (
          error && (
            <Error
              title={i18n.t("error")}
              subtitle={error?.toString()}
              actionText={i18n.t("retry_search")}
              onActionClick={onResubmitSearch}
            />
          )
        )}
        <ResultList>
          {renderResults()}
          {loading && <LoadingResultList count={isChatSearch ? 8 : 3} />}
        </ResultList>
      </>
    );
  },
);
SearchResults.displayName = "SearchResults";

const CenteredContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  max-height: 100%;
  overflow: auto;
`;
